import classNames from 'classnames';
import * as Kefir from 'kefir';
import kefirBus from 'kefir-bus';
import kefirStopper, { Stopper } from 'kefir-stopper';
import { ModifierFn, Modifiers } from 'popper.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MenuItem as MI, MenuItemProps as MIP, MenuList, MenuListInspector, ChosenEvent } from 'react-menu-list';
import { PopperProps } from 'react-popper';
import { HasClassName } from '../component-utils';
import Flyout from './Flyout';
import { ReactComponent as SubmenuIcon } from './icons/submenu.svg';
import './Menu.scss';
import { PortalPopper } from './PortalPopper';

// should match the value in Menu.scss
const VerticalPadding = 8

const PopperModifiers: Modifiers = {
  preventOverflow: { enabled: false },
  hide: { enabled: false },
  offset: {
    offset: -VerticalPadding,
  },
}

interface Point {
  x: number
  y: number
}

interface Rect extends Point {
  width: number
  height: number
}

interface EventEx {
  shouldIgnore?: boolean
}

export type MenuTarget = HTMLElement | Rect | null

type MenuProps = React.PropsWithChildren<HasClassName & {
  open: boolean
  onClose(): void
  target: MenuTarget
  placement?: PopperProps['placement']
  setWidth?: boolean
  onClick?(): void
  ignoreFocusEvents?: boolean
}>

export function Menu({ open, onClose, children, target, className, placement = 'bottom-start', setWidth, onClick, ignoreFocusEvents = false }: MenuProps) {
  const closeBus = useRef(kefirBus<void, void>())
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const handleOpen = useCallback(() => {
    // Clicking outside of the menu or pressing escape should close the menu
    Kefir.merge([
      Kefir.merge([
        fromEventsCapture(window, 'mousedown'),
        fromEventsCapture(window, 'focus'),
      ]),

      Kefir.fromEvents<KeyboardEvent, void>(window, 'keydown')
        .filter(e => e.key === 'Escape' || e.which === 27)
        .onValue(e => {
          e.preventDefault()
          e.stopPropagation()
        }),
    ]).takeUntilBy(closeBus.current)
      .onValue(e => {
        // giving React a chance to route the event through the portals first
        if (ignoreFocusEvents !== true || !(e instanceof FocusEvent)) {
          setTimeout(() => {
            if (!(e as EventEx).shouldIgnore) {
              onCloseRef.current()
            }
          }, 1)
        }
      })
  }, [ignoreFocusEvents])

  const handleClose = useCallback(() => {
    closeBus.current.emit()
  }, [])

  useEffect(() => {
    if (open) {
      handleOpen()
    } else {
      handleClose()
    }
  }, [handleClose, handleOpen, open])

  useEffect(() => {
    return () => handleClose()
  }, [handleClose])

  const visRef = useMemo(() => target && ('getBoundingClientRect' in target ? target : virtualReference(target)), [target])

  function ignoreEvent(e: React.SyntheticEvent) {
    (e.nativeEvent as EventEx).shouldIgnore = true
  }

  function stopEvent(e: React.SyntheticEvent) {
    e.stopPropagation()

    if (onClick)
      onClick()
  }

  if (!open) { return null }

  return <PortalPopper referenceElement={visRef || undefined} placement={placement} modifiers={{
    setWidth: {
      enabled: Boolean(setWidth),
      order: 840,
      fn: setWidthModifier,
    },
  }}>
    {({ ref, style }) =>
      <MenuListInspector onItemChosen={() => onClose()}>
        <Flyout className={classNames('Menu', className)} innerRef={ref} style={style} onClick={stopEvent} onMouseDown={ignoreEvent} onFocus={ignoreEvent}>
          <SubMenu>
            {children}
          </SubMenu>
        </Flyout>
      </MenuListInspector>}
  </PortalPopper>
}

export function virtualReference(rect: Rect) {
  return {
    getBoundingClientRect() {
      return {
        top: rect.y,
        left: rect.x,
        bottom: rect.y + rect.height,
        right: rect.x + rect.width,
        width: rect.width,
        height: rect.height,
      }
    },

    get clientWidth() {
      return rect.width
    },

    get clientHeight() {
      return rect.height
    },
  }
}

export function targetFromEvent(e: MouseEvent | React.MouseEvent): Rect {
  return { x: e.pageX, y: e.pageY, width: 0, height: 0 }
}

export function SubMenu({ children }: React.PropsWithChildren<{}>) {
  return <MenuList>
    {children}
  </MenuList>
}

type MenuItemProps = React.PropsWithChildren<{
  subMenu?: React.ReactElement | null
  onClick?(event: ChosenEvent): void
  disabled?: boolean
  disabledClassName?: string
  noIcon?: boolean
  preselect?: boolean
  itemClass?: string
}>

export function MenuItem({ disabled, disabledClassName, noIcon, children, onClick, subMenu, preselect, itemClass }: MenuItemProps) {
  const className = classNames('item', itemClass !== undefined && itemClass, disabled && 'disabled', disabled && disabledClassName, noIcon && 'noIcon')
  const menuRef = useRef<MI>(null)

  useEffect(() => {
    if (preselect && menuRef.current) {
      menuRef.current.highlight()
    }
  }, [preselect])

  if (disabled) {
    return <div className={className}>
      {children}
      {subMenu && <SubmenuIcon className='submenu-icon' />}
    </div>
  }

  const props: MIP = {
    className,
    highlightedClassName: 'active',
    onItemChosen: onClick,
    children,
  }

  if (subMenu) {
    return <SubMenuItem {...props} menu={subMenu} innerRef={menuRef}>
      {children}
      <SubmenuIcon className='submenu-icon' />
    </SubMenuItem>
  } else {
    return <MI {...props} ref={menuRef} />
  }
}

export function MenuDivider() {
  return <div className='MenuDivider' />
}

export function Section({ title }: { title: string }) {
  return <div className='MenuSection'><h4>{title}</h4></div >
}

// everything below is copied from react-menu-list and modified to use Popper

function fromEventsCapture(target: EventTarget, eventName: string): Kefir.Observable<Event, void> {
  return Kefir.stream(emitter => {
    target.addEventListener(eventName, emitter.emit, true)
    return () => {
      target.removeEventListener(eventName, emitter.emit, true)
    }
  })
}

function SubMenuItem({ index, className, highlightedClassName, children, menu, onItemChosen, innerRef }: MIP & { menu: React.ReactNode, innerRef?: React.MutableRefObject<MI | null> }) {
  const [opened, setOpened] = useState(false)

  const menuItemRef = useRef<MI | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuInspectorRef = useRef<MenuListInspector>(null)
  const menuContainerRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement | null>

  const resetMouseLeaveWatcher = useRef(kefirBus<void, void>())

  const stopperRef = useRef<Stopper>()

  useEffect(() => {
    const stopper = kefirStopper()
    stopperRef.current = stopper
    return () => stopper.destroy()
  })

  function open() {
    const menuItem = menuItemRef.current
    if (!menuItem) throw new Error()

    menuItem.lockHighlight()
    if (opened) return

    menuItem.takeKeyboard()
    setOpened(true)
  }

  function close() {
    const menuItem = menuItemRef.current
    if (!menuItem) throw new Error()

    if (!opened) return
    setOpened(false)
    menuItem.releaseKeyboard()
    menuItem.unlockHighlight()
  }

  type HighlightChangeArg = Parameters<NonNullable<MIP['onHighlightChange']>>[1]

  function onHighlightChange(highlighted: boolean, event: HighlightChangeArg) {
    resetMouseLeaveWatcher.current.emit()

    if (highlighted && !event.byKeyboard && stopperRef.current) {
      const OPEN_DELAY = 200

      Kefir.later(OPEN_DELAY, 0)
        .takeUntilBy(resetMouseLeaveWatcher.current)
        .takeUntilBy(stopperRef.current)
        .onValue(() => {
          open()
        })
    } else if (!highlighted) {
      close()
    }
  }

  function onMouseLeaveItem(event: MouseEvent) {
    const menuItem = menuItemRef.current
    if (!menuItem || !stopperRef.current) throw new Error()

    if (!opened) {
      menuItem.unhighlight()
      return
    }

    const menuContainer = menuContainerRef.current
    if (!menuContainer) throw new Error()

    // If the mouse isn't going toward the menu, then unhighlight ourself.

    const menuRect = menuContainer.getBoundingClientRect()

    const startTime = Date.now()
    const startX = event.pageX, startY = event.pageY

    function getDistance(x: number, y: number) {
      return pointRectDistance(x, y, menuRect.left, menuRect.top, menuRect.right - menuRect.width, menuRect.bottom - menuRect.top)
    }

    const startDistance = getDistance(startX, startY)
    let lastCoords = { pageX: startX, pageY: startY }

    // pixels per second the user must be moving the mouse toward the menu for
    // the menu to stay open.
    const MIN_SPEED = 60

    // ms before the menu will close if the user hasn't reached it yet, no
    // matter how they're moving the cursor toward it.
    const MAX_TIME = 750

    // ms to offset start time, to set maxDistance back a little so it's not so
    // unforgiving at the very start.
    const LEAD_TIME = 50

    // Listen to mouse moves, find the first event not going towards the menu,
    // and end it there. Or end after a timer.
    Kefir.fromEvents<MouseEvent, void>(window, 'mousemove')
      .bufferBy(Kefir.interval(60, null))
      .map(events => {
        if (events.length) {
          const last = events[events.length - 1]
          lastCoords = { pageX: last.pageX, pageY: last.pageY }
        }
        return lastCoords
      })
      .filter(({ pageX, pageY }) => {
        const distance = getDistance(pageX, pageY)
        const maxDistance = startDistance - (Date.now() - startTime - LEAD_TIME) / 1000 * MIN_SPEED
        return distance > maxDistance
      })
      .merge(Kefir.later(MAX_TIME * 1000, 0))
      .take(1)
      .takeUntilBy(resetMouseLeaveWatcher.current)
      .takeUntilBy(stopperRef.current)
      .onValue(() => {
        close()
        menuItem.unhighlight()
      })
  }

  function mouseEnterMenu() {
    const menuItem = menuItemRef.current
    if (!menuItem) throw new Error()

    resetMouseLeaveWatcher.current.emit()
    menuItem.unlockHighlight()
  }

  return <><MI
    ref={ref => {
      menuItemRef.current = ref
      if (innerRef) {
        innerRef.current = ref
      }
    }}
    domRef={anchorRef}
    index={index}
    className={className}
    highlightedClassName={highlightedClassName}
    onHighlightChange={onHighlightChange}
    onMouseLeave={onMouseLeaveItem}
    onRightPushed={e => {
      if (!opened) {
        e.stopPropagation()
        e.preventDefault()
        open()

        const menuInspector = menuInspectorRef.current
        if (!menuInspector) throw new Error()
        menuInspector.moveCursor('down')
      }
    }}
    onItemChosen={e => {
      if (onItemChosen) {
        onItemChosen(e)
        return
      }

      e.stopPropagation()
      e.preventDefault()
      open()
      if (e.byKeyboard) {
        const menuInspector = menuInspectorRef.current
        if (!menuInspector) throw new Error()
        menuInspector.moveCursor('down')
      }
    }}
    aria-haspopup={true}
    aria-expanded={opened}>
    {children}
  </MI>
    {opened && <PortalPopper referenceElement={anchorRef.current || undefined} placement='right-start' modifiers={PopperModifiers}>
      {({ ref, style, placement }) => <MenuListInspector ref={menuInspectorRef} onLeftPushed={e => {
        e.stopPropagation()
        e.preventDefault()
        close()
      }}>
        <Flyout className='Menu'
          innerRef={x => { (ref as React.RefCallback<any>)(x); menuContainerRef.current = x }} style={style} data-placement={placement}
          onMouseEnter={mouseEnterMenu}>
          {menu}
        </Flyout>
      </MenuListInspector>}
    </PortalPopper>}</>
}

function pointRectDistance(px: number, py: number, rx: number, ry: number, rwidth: number, rheight: number): number {
  const cy = Math.max(Math.min(py, ry + rheight), ry)
  const cx = Math.max(Math.min(px, rx + rwidth), rx)
  return Math.sqrt((px - cx) * (px - cx) + (py - cy) * (py - cy))
}

const setWidthModifier: ModifierFn = (data) => {
  data.offsets.popper.left = data.offsets.reference.left
    ; (data.offsets.popper as any).right = (data.offsets.reference as any).right

  const width = Math.round(data.offsets.reference.width)
  data.offsets.popper.width = width
  data.styles.width = `${width}px`

  return data;
}
